import { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import {
  Stack,
  Text,
  Button,
  Flex,
  Box,
  Card,
  HStack,
  Icon,
  IconButton,
  Table,
  ButtonGroup,
  Spinner,
} from "@chakra-ui/react";
import { Link, useParams } from "react-router-dom";
import {
  AppHeader,
  AppHeaderHeight,
  AppHeaderLogo,
} from "../../../shared/views/AppHeader/AppHeader";
import { useOsmToolsMap } from "./useOsmToolsMap";
import { useStores } from "../../../shared/stores/RootStore";
import { ArrowRightIcon, CloseIcon } from "../../../shared/views/icons";
import { appRoutes } from "../../../../App";
import { StreetViewComponent } from "../../../hla/views/StreetView/StreetViewComponent";
import { TranslateTagDialog } from "./TranslateTagDialog";
import {
  defaultStrokeColor,
  mapGradientStep,
} from "../../../map/utils/map-utils";
import { toast } from "../../../shared/views/CustomToaster";

const AdminOsmToolsPageBase = () => {
  const { osmToolsStore } = useStores();
  const { cityId } = useParams();
  const [translateTag, setTranslateTag] = useState<{
    key: string;
    value: string;
  }>();
  const [loading, setLoading] = useState(false);

  const { city, data, polygons, selectedWay, allTags, highlightedTag } =
    osmToolsStore;

  useOsmToolsMap();

  useEffect(() => {
    osmToolsStore.resetState();
    osmToolsStore.fetchCity(cityId!);

    setLoading(true);
    osmToolsStore.fetchData(cityId!).finally(() => {
      setLoading(false);
    });
    // eslint-disable-next-line
  }, []);

  return (
    <Flex flexDir="column" maxHeight="100vh" flexGrow={1}>
      <AppHeader>
        <HStack
          gap={4}
          separator={<Icon as={ArrowRightIcon} borderInline={"none"} />}
          flexGrow={1}
        >
          <AppHeaderLogo />

          <Link to={appRoutes.adminCities()}>
            <Text fontSize="sm" fontWeight="semibold">
              Cities
            </Text>
          </Link>

          {!!city && (
            <Text fontSize="sm" fontWeight="semibold">
              {city?.name} ({city?.country})
            </Text>
          )}
        </HStack>
      </AppHeader>
      <AppHeaderHeight />

      <Flex flexGrow={1} overflow="hidden">
        <Stack w={"400px"} overflow="auto" alignItems={"stretch"} p={3} gap={4}>
          <Button
            size={"sm"}
            colorPalette={"primary"}
            disabled={!city || !!polygons}
            onClick={() => osmToolsStore.searchPolygon()}
          >
            Fetch city polygon
          </Button>

          {polygons?.map((p, index) => (
            <Stack key={index}>
              <HStack>
                {polygons.length > 1 && (
                  <Box
                    boxSize={"10px"}
                    borderRadius={"10px"}
                    border={`1px solid ${defaultStrokeColor}`}
                    bgColor={mapGradientStep(polygons.length, index)}
                  />
                )}

                <Text fontSize={"xs"} textTransform={"uppercase"} flexGrow={1}>
                  Polygon #{index}
                </Text>

                <ButtonGroup
                  fontSize={"sm"}
                  size={"sm"}
                  colorPalette={"primary"}
                  attached
                  variant={"outline"}
                >
                  <Button
                    loading={osmToolsStore.querying}
                    onClick={() => {
                      osmToolsStore.queryPolygon(
                        cityId!,
                        p.geometry.coordinates.flat(),
                      );
                    }}
                  >
                    Query OSM
                  </Button>
                  <Button
                    onClick={() => {
                      osmToolsStore.splitPolygon(index).then(() => {
                        toast("success", `Added polygon #${index}`);
                      });
                    }}
                  >
                    Split
                  </Button>
                </ButtonGroup>
              </HStack>
            </Stack>
          ))}

          {!!data?.ways.length && (
            <Button
              size={"sm"}
              colorPalette={"primary"}
              disabled={!city}
              onClick={() => osmToolsStore.deleteData(cityId!)}
            >
              Clear data
            </Button>
          )}

          {loading && (
            <HStack>
              <Spinner size={"sm"} />
              <Text fontSize={"sm"}>Loading data...</Text>
            </HStack>
          )}

          {!!data && (
            <Stack pt={4}>
              <Text>Saved data</Text>
              <Text fontSize={"sm"}>
                {data?.ways?.length} ways • {data?.nodes?.length} nodes
              </Text>
            </Stack>
          )}

          {!!allTags && (
            <Table.Root size="sm" maxWidth={"100%"}>
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader>Key</Table.ColumnHeader>
                  <Table.ColumnHeader>Value</Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {Object.entries(allTags)
                  .toSorted((a, b) => a[0].localeCompare(b[0]))
                  .map(([key, values]) => (
                    <Table.Row key={key}>
                      <Table.Cell maxWidth={"160px"}>{key}</Table.Cell>
                      <Table.Cell>
                        <Stack>
                          {values.map((value) => {
                            const selected =
                              highlightedTag?.key === key &&
                              highlightedTag?.value === value;
                            return (
                              <Text
                                key={value}
                                maxWidth={"200px"}
                                color={selected ? "#c320e0" : "black"}
                                fontWeight={selected ? "semibold" : "normal"}
                                _hover={{ textDecor: "underline" }}
                                cursor={"pointer"}
                                onClick={() => {
                                  if (selected) {
                                    setTranslateTag({ key, value });
                                  } else {
                                    osmToolsStore.highlightTag(key, value);
                                  }
                                }}
                              >
                                {value}
                              </Text>
                            );
                          })}
                        </Stack>
                      </Table.Cell>
                    </Table.Row>
                  ))}
              </Table.Body>
            </Table.Root>
          )}
        </Stack>

        <Stack flexGrow={1} position={"relative"}>
          <Box id="map" flexGrow={1} height="100%" />

          <Box position={"absolute"} bottom={3} right={3}>
            {!!selectedWay && (
              <Card.Root w={"400px"} overflow={"hidden"}>
                <Stack p={3}>
                  <HStack>
                    <Text flexGrow={1} fontSize={"sm"} fontWeight={"semibold"}>
                      Way #{selectedWay.id}
                    </Text>
                    <IconButton
                      size="sm"
                      variant={"ghost"}
                      onClick={() => osmToolsStore.selectWay(undefined)}
                    >
                      <CloseIcon />
                    </IconButton>
                  </HStack>

                  <Table.Root size={"sm"}>
                    <Table.Header>
                      <Table.Row>
                        <Table.ColumnHeader>Key</Table.ColumnHeader>
                        <Table.ColumnHeader>Value</Table.ColumnHeader>
                      </Table.Row>
                    </Table.Header>

                    <Table.Body>
                      {Object.entries(selectedWay.tags).map(([key, value]) => (
                        <Table.Row key={key}>
                          <Table.Cell>{key}</Table.Cell>
                          <Table.Cell>{value}</Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table.Root>
                </Stack>

                <Box h={"300px"}>
                  <StreetViewComponent
                    streetViewMapSource={osmToolsStore.streetViewMapSource}
                  />
                </Box>
              </Card.Root>
            )}
          </Box>
        </Stack>
      </Flex>

      {!!translateTag && (
        <TranslateTagDialog
          cityId={cityId!}
          tag={translateTag}
          onClose={() => setTranslateTag(undefined)}
        />
      )}
    </Flex>
  );
};

export const AdminOsmToolsPage = observer(AdminOsmToolsPageBase);
